import React from 'react';
import { useModal } from 'hooks/useModal';
import { useQuery } from '@apollo/client';
import { ModalsKeys, Nullable, SelectOption } from 'global';
import { DialogHeader } from 'components/shared/Dialog/DialogHeader';
import { CircularProgress, Dialog, DialogContent, Stack } from '@mui/material';
import { RenderWithFallback } from 'components/shared/Conditional/RenderWithFallback';

import { ActivityType } from '../../types';
import { DealActivityQuery } from '../../api/types';
import { CallActivityForm } from '../form/CallActivityForm';
import { CUSTOMER_DEAL_ACTIVITIES_QUERY_NAME, dealActivityApi, useDealActivityMutations } from '../../api';

export const CallActivityDialog = () => {
  const { handleClose, isOpen, data } = useModal(ModalsKeys.CallActivityDialog);

  const { data: response, loading } = useQuery<DealActivityQuery>(dealActivityApi.dealActivity, {
    skip: !data?.entityId,
    fetchPolicy: 'network-only',
    variables: {
      input: {
        id: data.entityId,
      },
    },
  });

  const { handleCreateDealActivity, handleUpdateDealActivity, isDealActivityCreating, isDealActivityUpdating } =
    useDealActivityMutations();

  const dealActivity = response?.dealActivity?.result?.entity;

  if (!!dealActivity && dealActivity?.type !== ActivityType.CALL) return null;

  const contact = dealActivity?.participants?.find((participant: any) => participant?.participationType === 'EXTERNAL');

  const isEditMode = Boolean(dealActivity);

  const handleSubmit = async (formValues: {
    status: string;
    subject: string;
    direction: string;
    description: string;
    contact: Nullable<SelectOption>;
  }) => {
    const commonVariables = { type: ActivityType.CALL, parentType: data.parentType, parentId: data.parentId };

    if (!dealActivity?.id) {
      await handleCreateDealActivity(
        {
          ...commonVariables,
          call: {
            status: formValues.status,
            subject: formValues.subject,
            callDirection: formValues.direction,
            description: formValues.description,
            contactId: formValues.contact!.value,
          },
        },
        api => ({
          awaitRefetchQueries: true,
          refetchQueries: [api.dealActivities, CUSTOMER_DEAL_ACTIVITIES_QUERY_NAME],
        })
      );
    } else {
      await handleUpdateDealActivity(
        {
          ...commonVariables,
          id: dealActivity.id,
          call: {
            status: formValues.status,
            subject: formValues.subject,
            callDirection: formValues.direction,
            description: formValues.description,
          },
        },
        api => ({
          awaitRefetchQueries: true,
          refetchQueries: [api.dealActivities, CUSTOMER_DEAL_ACTIVITIES_QUERY_NAME],
        })
      );
    }

    handleClose();
  };

  return (
    <Dialog open={isOpen} onClose={handleClose} fullWidth maxWidth="lg">
      <DialogHeader label={dealActivity || loading ? 'Edit Call' : 'Add New Call'} handleClose={handleClose} />

      <DialogContent>
        <Stack sx={{ paddingTop: '0.5rem' }}>
          <RenderWithFallback
            isLoading={loading}
            fallBack={
              <Stack height="240px" alignItems="center" justifyContent="center">
                <CircularProgress />
              </Stack>
            }
          >
            <CallActivityForm
              isEdit={isEditMode}
              onCancel={handleClose}
              onSubmit={handleSubmit}
              isSubmitting={isDealActivityCreating || isDealActivityUpdating}
              initialValues={
                dealActivity && contact
                  ? {
                      subject: dealActivity.topic,
                      status: dealActivity.callStatus,
                      direction: dealActivity.callDirection,
                      description: dealActivity.description,
                      contact: { label: contact.fullName, value: contact.id },
                    }
                  : null
              }
            />
          </RenderWithFallback>
        </Stack>
      </DialogContent>
    </Dialog>
  );
};
